import { useContext, useEffect, useMemo } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import {
  resolvePrivacyModeFromParams,
  persistPrivacyMode,
  readPersistedPrivacyMode,
} from '../utils/privacyMasking';
import { usePresentationStore } from '../stores/presentationStore';

function useSafeSearchParams(): URLSearchParams {
  const locationCtx = useContext(UNSAFE_LocationContext);
  const search = locationCtx?.location
    ? (locationCtx.location.search ?? '')
    : typeof window !== 'undefined'
      ? window.location.search
      : '';
  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * The single, unified read of the operational privacy/overhead mode.
 *
 * This is the ONE helper every surface (dashboard widgets AND their drill-down
 * destinations) should call to decide whether to mask customer identity. It
 * reconciles the historically-separate flags into one fail-safe source:
 *
 *   1. URL params — `?privacy=on` or `?display=overhead` (the AOC wallboard
 *      convention). When present, the state is persisted to sessionStorage AND
 *      switches the presentation store on, so it SURVIVES every navigation out
 *      of the masked surface. A drill-down page never carries the param, but it
 *      still reads the persisted flag and masks — no param-threading, no leaks.
 *   2. The persisted sessionStorage flag (set by #1 above).
 *   3. The in-app presentation-mode store (`isPresentationMode`), the global
 *      toggle that already survives navigation on its own.
 *
 * Any of the three ON => masked. THE URL CAN ONLY EVER TURN PRIVACY ON. There is
 * no URL value that turns it off: `?privacy=off` is ignored. Turning privacy off
 * takes the Presentation PIN (usePresentationStore.disablePresentationMode),
 * which clears all three signals together (the toolbar toggle then also drops
 * the privacy/display params from the address). Settings promises "the PIN is
 * required both to enable masking and to reveal the data again"; an address
 * anyone can type must not undo that.
 */
/**
 * The persistence side of the privacy mode: read the URL params and persist an
 * activation. Mount this ONCE at a shared route boundary (the authenticated
 * layout) so the state is captured on EVERY route — including surfaces like the
 * AOC control tower that derive privacy locally and never call `usePrivacyMode`.
 * Without this, opening `/aoc?display=overhead` in a fresh tab would never
 * persist, and a drill-down to a page that only reads the persisted flag would
 * show raw PII.
 */
export function usePrivacyModePersistence(): void {
  const searchParams = useSafeSearchParams();
  const fromUrl = resolvePrivacyModeFromParams(searchParams);

  useEffect(() => {
    if (!fromUrl) return;
    persistPrivacyMode(true);
    // The same switch as the toolbar: from now on only the PIN turns it off.
    usePresentationStore.getState().activateFromLink();
  }, [fromUrl]);
}

export function usePrivacyMode(): boolean {
  // Also persist locally so components that call the hook keep working even if
  // the shared boundary is ever bypassed.
  usePrivacyModePersistence();

  const searchParams = useSafeSearchParams();
  const isPresentationMode = usePresentationStore((state) => state.isPresentationMode);

  const fromUrl = resolvePrivacyModeFromParams(searchParams);

  return useMemo(
    () => fromUrl || readPersistedPrivacyMode() || isPresentationMode,
    [fromUrl, isPresentationMode],
  );
}
